import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { useShipment } from '@/api/rpc/hooks/supply-chain/useShipments';
import {
  TruckIcon,
  ArrowSquareOutIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
} from '@phosphor-icons/react';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { Badge } from '@/components/ui/badge';

interface ShipmentPreviewProps {
  entityType: SystemEntity; // Required by preview component registry but not used - type is always SCM_SHIPMENT
  entityId: string;
  className?: string;
}

const STATUS_LABELS: Record<string, string> = {
  need_to_be_scheduled: 'Need to be Scheduled',
  scheduled: 'Scheduled',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  need_to_be_scheduled: 'outline',
  scheduled: 'secondary',
  in_transit: 'default',
  delivered: 'default',
  cancelled: 'destructive',
};

export function ShipmentPreview({ entityId, className }: ShipmentPreviewProps) {
  const {
    data: shipmentData,
    isLoading,
    error,
    salesOrders,
    chargesCount,
  } = useShipment(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <TruckIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Shipment</h4>
        </div>
        <p className="text-xs text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !shipmentData?.shipment) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <TruckIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">Shipment</h4>
        </div>
        <p className="text-xs text-muted-foreground">
          Unable to load shipment details
        </p>
      </div>
    );
  }

  const shipment = shipmentData.shipment;

  // Prefer originLoadNumber, fallback to originLoadId
  const loadIdentifier =
    shipment.originLoadNumber ||
    shipment.originLoadId ||
    shipment.id.slice(0, 8);

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <TruckIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">Shipment {loadIdentifier}</h4>
      </div>

      <div className="space-y-2 text-xs mb-3">
        <div className="flex items-center justify-between">
          <Badge
            variant={STATUS_VARIANTS[shipment.status] || 'outline'}
            className="text-xs"
          >
            {STATUS_LABELS[shipment.status] || shipment.status}
          </Badge>
          {shipment.type && (
            <Badge variant="outline" className="text-xs capitalize">
              {shipment.type}
            </Badge>
          )}
        </div>

        {(shipment.origin || shipment.destination) && (
          <div>
            <span className="text-muted-foreground">Route: </span>
            <span className="font-medium">
              {shipment.origin ?? '?'} → {shipment.destination ?? '?'}
            </span>
          </div>
        )}

        {shipment.scheduledAt && (
          <div>
            <span className="text-muted-foreground">Scheduled: </span>
            <span className="font-medium">
              {formatDateOnlyString(shipment.scheduledAt)}
            </span>
          </div>
        )}

        {salesOrders.length > 0 && (
          <div className="flex items-center gap-1">
            <ShoppingCartIcon className="size-3 text-muted-foreground" />
            <span className="text-muted-foreground">Sales Orders: </span>
            <span className="font-medium">{salesOrders.length}</span>
          </div>
        )}

        {chargesCount > 0 && (
          <div className="flex items-center gap-1">
            <CurrencyDollarIcon className="size-3 text-muted-foreground" />
            <span className="text-muted-foreground">Charges: </span>
            <span className="font-medium">{chargesCount}</span>
          </div>
        )}
      </div>

      <div className="mt-3">
        <a
          href={`/supply-chain/shipments/${shipment.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full px-3 py-1.5 text-xs font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
        >
          <ArrowSquareOutIcon className="size-3 mr-1.5" />
          View Details
        </a>
      </div>
    </div>
  );
}
